import React from "react";
import { Alert, Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Input from "@components/Input/Input";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { usePreApproveDialog } from "@features/bay-hire/hooks/usePreApproveDialog";
import {
  PRE_APPROVE_DIALOG_FIELDS as F,
  type PreApproveDialogFormData,
} from "@features/bay-hire/schema/preApproveDialogSchema";
import { bayHirePaymentTypeLabel } from "@features/bay-hire/utils/bayHireLabels";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

interface PreApproveDialogProps {
  open: boolean;
  requestId: string;
  numberOfBays?: number | null;
  /** Vendor-requested window; seeds the approved dates so Ops adjusts rather than retypes. */
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Already-approved window, if this request has been pre-approved before. */
  dateFromApproved?: string | null;
  dateToApproved?: string | null;
  /** Drives the chargeable Total Amount rule - see preApproveDialogSchema.ts. */
  paymentType?: string | null;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

const PreApproveDialog: React.FC<PreApproveDialogProps> = ({
  open,
  requestId,
  numberOfBays,
  dateFrom,
  dateTo,
  dateFromApproved,
  dateToApproved,
  paymentType,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const uploadConditions = useFileUploadProfiles().bayHireAttachment;

  const {
    form,
    hodStagedAttachment,
    hodFileName,
    submitError,
    isSubmitting,
    showConfirm,
    isHodStaging,
    hodFiles,
    setHodFiles,
    handleHodUpload,
    handleViewHodDocument,
    handleRemoveHodDocument,
    supportingDocumentStagedAttachment,
    supportingDocumentFileName,
    supportingDocumentFiles,
    setSupportingDocumentFiles,
    handleSupportingDocumentUpload,
    handleRemoveSupportingDocument,
    isSupportingDocumentStaging,
    handleSubmitForm,
    handleConfirmSubmit,
    handleCancelConfirm,
    handleClose,
  } = usePreApproveDialog({
    open,
    requestId,
    dateFrom,
    dateTo,
    dateFromApproved,
    dateToApproved,
    paymentType,
    onClose,
    onSuccess,
  });

  const { handleSubmit } = form;

  return (
    <div data-testid="pre-approve-dialog">
      <SideDrawer
        isOpen={open}
        title={t("bay-hire.pre-approve.title")}
        onClose={handleClose}
        onSubmit={handleSubmit(handleSubmitForm)}
        isSubmitting={isSubmitting}
        submitLabel={t("bay-hire.pre-approve.submit")}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
      >
        <div className="flex flex-col gap-1 px-6 py-2">
          {numberOfBays != null && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
              data-testid="pre-approve-number-of-bays"
            >
              <Trans
                i18nKey="bay-hire.pre-approve.vendor-selected-bays"
                values={{ count: numberOfBays }}
                defaults="Shipping Agent selected <strong>{{count}}</strong> bay(s)."
                components={{ strong: <strong /> }}
              />
            </Typography>
          )}

          {/* Megaplan (2026-08-05): drawer field order changed to match the wireframe
              (05_Port_License_details-19.png, "PoF Visuals_Bay Hire 2") - Chargeable, Payment
              type, Total Amount, then uploads/Remarks unchanged. Chargeable is rendered as a
              radio pair (matching the wireframe's Yes/No radios) but stays READ-ONLY/disabled -
              it is derived server-side from paymentType (D42's own rule: EXEMPTED -> not
              chargeable, MONTHLY/YEARLY -> chargeable) and is NOT resubmitted as an editable
              control, which D42 deliberately removed from the contract; only the visual
              presentation changed here, not the underlying behavior. Payment type is likewise
              read-only, now styled to match Total Amount's own labeled-field look (CustomFormLabel
              + disabled Input) instead of the plain text it used to be. */}
          {paymentType && (
            <Box sx={{ mb: 2 }} data-testid="pre-approve-chargeable">
              {/* Matches FormItem.tsx's own label wrapper (mb: 1) - a bare CustomFormLabel with no
                  spacing sat flush against the control below it. */}
              <Box sx={{ mb: 1 }}>
                <CustomFormLabel>{t("bay-hire.detail.chargeableLabel")}</CustomFormLabel>
              </Box>
              <RadioGroup row value={paymentType !== "EXEMPTED"}>
                <FormControlLabel
                  value
                  control={<Radio />}
                  label={
                    <CustomFormLabel as="span">
                      {t("common.yes", { defaultValue: "Yes" })}
                    </CustomFormLabel>
                  }
                  disabled
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label={
                    <CustomFormLabel as="span">
                      {t("common.no", { defaultValue: "No" })}
                    </CustomFormLabel>
                  }
                  disabled
                />
              </RadioGroup>
            </Box>
          )}

          {paymentType && (
            <Box sx={{ mb: 2 }}>
              {/* Matches FormItem.tsx's own label wrapper (mb: 1) - a bare CustomFormLabel with no
                  spacing sat flush against the input below it. */}
              <Box sx={{ mb: 1 }}>
                <CustomFormLabel>{t("bay-hire.table.paymentType")}</CustomFormLabel>
              </Box>
              <Input
                fullWidth
                disabled
                value={bayHirePaymentTypeLabel(t, paymentType)}
                testId="pre-approve-payment-type"
              />
            </Box>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="pre-approve-submit-error">
              {submitError}
            </Alert>
          )}

          {/* DFT-0044 slice C: the bay-selection combobox is REMOVED. Operations no longer picks
              bays - the Shipping Agent selects them at request time and they are immutable
              thereafter (D35/D43), so `bayAssignments` is gone from the pre-approve contract.
              Leaving the control would have let Operations choose bays that the server silently
              discards, which is worse than not offering the choice.

              ⚠️ FOLLOW-UP: Operations should still SEE the vendor's chosen bays here, read-only.
              This dialog is only passed `numberOfBays`, not the request's `bayAssignments`, so that
              needs a new prop threaded from the caller. Deliberately not invented here - the plan's
              wording on read-only bays in this drawer is ambiguous, and guessing a layout is worse
              than naming the gap. */}

          {/* Megaplan (2026-08-05): Date From Approved / Date To Approved are REMOVED from the UI,
              matching the wireframe (05_Port_License_details-19.png) exactly - it goes straight
              from Chargeable/Payment type to Total Amount, with no approved-date fields at all.
              This REVERSES the earlier "flagged for design review" decision recorded below (kept
              at the time because dateFromApproved/dateToApproved are REQUIRED on
              PreApproveRequest, bay-hire-bff-api.yaml) - the resolution is that Operations no
              longer adjusts the approved window through this dialog; it silently approves the
              vendor's REQUESTED window as-is. This still satisfies the required contract fields
              without any visible control: usePreApproveDialog's own `open` effect already seeds
              `dateFromApproved`/`dateToApproved` from the `dateFrom`/`dateTo` props (or a prior
              approval's own dates, if this request has been pre-approved before) on every open,
              and `handleSubmitForm` submits that seeded form state as-is - removing the FormItem
              here does not remove the underlying value, only the ability to see or edit it. */}
          <FormProvider {...form}>
            {/* DFT-0044 slice D (D42): the Chargeable checkbox is REMOVED, not disabled. chargeable
                is derived from the request's paymentType server-side - EXEMPTED false, MONTHLY and
                YEARLY true - and is no longer on the pre-approve contract at all. A disabled control
                would still imply Operations has a say; "Ops can only reject the request, cannot
                change the option" means there is no control to show. */}

            <FormItem<PreApproveDialogFormData, typeof F.TOTAL_AMOUNT>
              name={F.TOTAL_AMOUNT}
              type="number"
              label={t("bay-hire.pre-approve.totalAmount")}
              testId="pre-approve-total-amount"
              // DFT-0081: total_amount is NUMERIC(12,2) on the domain side (10 integer digits,
              // 2 decimal places) - maxDigits/decimals mirror that capacity, and
              // canBeginWithZero={false} matches the same flag already used the same way
              // elsewhere (e.g. vessel-nomination's numeric fields).
              maxDigits={10}
              decimals={2}
              canBeginWithZero={false}
            />
          </FormProvider>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("bay-hire.pre-approve.hod-section-title")}
            </Typography>
            {hodStagedAttachment ? (
              // DFT-0088 (G4): matches Cancel/Add Receipt's SingleFile presentation exactly;
              // AD2's additive onView/viewLabel props cover this section's View action (unique
              // to HOD among Bay Hire's upload slots), so it need not stay on a bespoke card.
              <SingleFile
                testId="pre-approve-hod"
                file={hodFiles[0] ?? { name: hodFileName ?? "" }}
                fieldName="pre-approve-upload-hod"
                setFiles={() => setHodFiles([])}
                removeFn={handleRemoveHodDocument}
                onView={() => void handleViewHodDocument()}
                viewLabel={t("bay-hire.pre-approve.view-hod")}
                isLoading={isHodStaging}
                disabled={isSubmitting}
              />
            ) : (
              <FileUpload
                testId="pre-approve-upload-hod"
                // DFT-0096: keyed by attachment type (mirrors
                // SingleCertificate.tsx's useUploadStore key convention) so a
                // staged-but-not-yet-submitted upload survives a refresh via
                // useUploadStore (FileUpload's own built-in integration - see
                // its `name` prop). handleHodUpload overwrites this key's
                // entry with the real {name, url} once the upload succeeds.
                name="HOD_APPROVAL"
                onUpload={(selected) => void handleHodUpload(selected)}
                onRemoveFile={handleRemoveHodDocument}
                files={hodFiles}
                setFiles={setHodFiles}
                maxFiles={1}
                allowedExtensions={(uploadConditions.format ?? []).map((ext) => `.${ext}`)}
                conditions={uploadConditions}
                disabled={isHodStaging}
                // DFT-0088 addendum (2026-09-01): matches the actual `bayHireAttachment`
                // allow-list - see FileUpload.constants.ts's own comment on that profile.
                uploadDescription={t("upload.dragOrDropDescription")}
              />
            )}
          </Box>

          {/* DFT-0044 (FE-73): a second, generic Attachments upload, separate from the HOD
              document above - `05_Port_License_details-19.png` shows both. Optional, never a
              gate on submit, posted as SUPPORTING_DOCUMENT (mirrors Reject/Cancel's own optional
              upload, F3) - needs no change to the pre-approve payload itself, since the
              attachment is recorded against the request directly. */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("bay-hire.pre-approve.attachments-section-title")}
            </Typography>
            {supportingDocumentStagedAttachment ? (
              // DFT-0088 (G4): matches Cancel/Add Receipt's SingleFile presentation exactly -
              // Remove-only, this slot never had a View action.
              <SingleFile
                testId="pre-approve-attachment"
                file={supportingDocumentFiles[0] ?? { name: supportingDocumentFileName ?? "" }}
                fieldName="pre-approve-upload-attachment"
                setFiles={() => setSupportingDocumentFiles([])}
                removeFn={handleRemoveSupportingDocument}
                isLoading={isSupportingDocumentStaging}
                disabled={isSubmitting}
              />
            ) : (
              <FileUpload
                testId="pre-approve-upload-attachment"
                name="SUPPORTING_DOCUMENT"
                onUpload={(selected) => void handleSupportingDocumentUpload(selected)}
                onRemoveFile={handleRemoveSupportingDocument}
                files={supportingDocumentFiles}
                setFiles={setSupportingDocumentFiles}
                maxFiles={1}
                allowedExtensions={(uploadConditions.format ?? []).map((ext) => `.${ext}`)}
                conditions={uploadConditions}
                disabled={isSupportingDocumentStaging}
                // DFT-0088 addendum (2026-09-01): matches the actual `bayHireAttachment`
                // allow-list - see FileUpload.constants.ts's own comment on that profile.
                uploadDescription={t("upload.dragOrDropDescription")}
              />
            )}
          </Box>

          {/* 05_Port_License_details-19.png order: Total Amount -> HOD -> Attachments -> Remarks
              (Chargeable, between Total Amount and HOD in the wireframe, is deliberately absent -
              D42, see the comment above). Remarks is a second FormProvider, not a stray field
              outside the form - both share the same `form` context; RHF only needs ONE physical
              form element, not one FormProvider per field group. */}
          <FormProvider {...form}>
            <FormItem<PreApproveDialogFormData, typeof F.PRE_APPROVAL_REMARKS>
              name={F.PRE_APPROVAL_REMARKS}
              type="textarea"
              label={t("bay-hire.pre-approve.preApprovalRemarks")}
              testId="pre-approve-remarks"
              minRows={2}
            />
          </FormProvider>
        </div>
      </SideDrawer>
      {/* DFT-0044 (FE-60): `05_Port_License_details-20.png` (PoF Visuals_Bay Hire 2) - a "Confirm
          Bay Hire Pre-Approval" step between the drawer's own Pre-approve submit and the request
          actually being pre-approved, matching Gatepass's ApproveGatepassDrawer two-step confirm.
          isInsideDrawer stacks it above the still-open SideDrawer (same z-index convention that
          component already exposes for exactly this case). */}
      <GenericDialog
        open={showConfirm}
        isInsideDrawer
        testId="pre-approve-confirm-dialog"
        title={t("bay-hire.pre-approve.confirm-title")}
        elementContent={t("bay-hire.pre-approve.confirm-message")}
        cancelButtonText={t("common.cancel", { defaultValue: "Cancel" })}
        confirmButtonText={t("bay-hire.pre-approve.submit")}
        handleCloseDialog={handleCancelConfirm}
        confirmAction={() => void handleConfirmSubmit()}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default PreApproveDialog;
